import styled from '@emotion/styled';
import { Typography } from '@innovamat/glimmer-components';
import type { ReactNode } from 'react';
import { Trans } from 'react-i18next';

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  white-space: pre-line;

  li::marker {
    font-size: 12px;
  }

  li {
    margin-left: 16px;
  }

  ul {
    margin-top: -16px;
    margin-bottom: 0;
  }
`;

const StyledBody = styled(Typography.Body2)`
  align-items: center;
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
  white-space: pre-line;

  p + p {
    margin-top: 8px;
  }
`;

const Image = styled.img`
  width: 160px;
  height: auto;
  object-fit: contain;
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TextWithImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 100%;
`;

const TextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

type KnowMoreSectionProps = {
  title?: string;
  titleTranslationKey?: string;
  bodyTranslationKey?: string;
  children?: ReactNode;
  image?: {
    src: string;
    alt: string;
    position?: 'left' | 'right';
  };
};

function KnowMoreSection({
  title,
  titleTranslationKey,
  bodyTranslationKey,
  children,
  image,
}: KnowMoreSectionProps): JSX.Element {
  return (
    <Section>
      {(title || titleTranslationKey) && (
        <Typography.Subtitle1>
          {titleTranslationKey ? (
            <Trans
              i18nKey={titleTranslationKey}
              defaults={titleTranslationKey}
              shouldUnescape={true}
              components={{
                b: <b />,
                br: <br />,
                li: <li />,
                ul: <ul />,
              }}
            />
          ) : (
            title
          )}
        </Typography.Subtitle1>
      )}
      <ContentContainer>
        <TextContent>
          <TextWithImageContainer>
            {image?.position === 'left' && image && (
              <Image src={image.src} alt={image.alt} />
            )}
            <div>
              {bodyTranslationKey && (
                <StyledBody>
                  <Trans
                    i18nKey={bodyTranslationKey}
                    defaults={bodyTranslationKey}
                    shouldUnescape={true}
                    components={{
                      b: <b />,
                      br: <br />,
                      li: <li />,
                      ul: <ul />,
                    }}
                  />
                </StyledBody>
              )}
              {children}
            </div>
            {image?.position !== 'left' && image && (
              <Image src={image.src} alt={image.alt} />
            )}
          </TextWithImageContainer>
        </TextContent>
      </ContentContainer>
    </Section>
  );
}

export { KnowMoreSection };
